import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export function useUnreadCount(pollMs = 30000) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!user || user.role === "ADMIN") return;
    let alive = true;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/api/notifications/unread-count");
        if (alive) setUnread(data.unread || 0);
      } catch (_) {}
    };
    fetchCount();
    const t = setInterval(fetchCount, pollMs);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [user, pollMs]);
  return [unread, setUnread];
}

export default function NotificationBell({ tone = "light" }) {
  const { user } = useAuth();
  const [unread] = useUnreadCount();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open || !user || user.role === "ADMIN") return;
    api
      .get("/api/notifications", { params: { limit: 10 } })
      .then((r) => setItems(r.data || []))
      .catch(() => {});
  }, [open, user]);

  if (!user || user.role === "ADMIN") return null;
  const listPath = user.role === "WORKER" ? "/worker/notifications" : "/customer/notifications";
  const dark = tone === "dark";

  return (
    <div className="relative flex-shrink-0">
      <button
        onClick={() => setOpen(!open)}
        className={`relative p-2 rounded-xl transition ${
          dark ? "hover:bg-white/15" : "hover:bg-brand-50"
        }`}
        data-testid="notif-bell"
        aria-label="Notifikasi"
      >
        <Bell className={`h-5 w-5 ${dark ? "text-white" : "text-brand-700"}`} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[11px] font-bold grid place-items-center"
            data-testid="notif-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-w-[90vw] bg-white rounded-2xl shadow-lg border border-brand-100 py-2 z-40">
          <div className="flex items-center justify-between px-4 py-2 border-b border-brand-50">
            <p className="text-sm font-bold text-brand-800">Notifikasi</p>
            <Link
              to={listPath}
              onClick={() => setOpen(false)}
              className="text-xs font-semibold text-brand-600 hover:underline"
            >
              Lihat semua
            </Link>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 && (
              <p className="px-4 py-6 text-sm text-brand-400 text-center">Belum ada notifikasi.</p>
            )}
            {items.map((n) => (
              <div
                key={n.id}
                className={`px-4 py-2.5 border-b border-brand-50 ${!n.read_at ? "bg-brand-50/50" : ""}`}
              >
                <p className="text-sm font-semibold text-brand-800">{n.title}</p>
                <p className="text-xs text-brand-500 line-clamp-2">{n.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
